import {
  DisplayEntitySnapshot,
  EntitySnapshotTable,
  SnapshotStatus
} from "modules/tenants/sections/pros/data-sync/entity-snapshots/EntitySnapshotTable";
import { useMemo, useState } from "react";

import { DateTime } from "@bps/date-time";
import {
  Heading,
  MessageBar,
  MessageBarType,
  SearchBox,
  Stack,
  StackItem,
  useTheme
} from "@bps/fluent-ui";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";
import { EntitySnapshot } from "@libs/api/gateways/tg/tg-ops-gateway.dtos";
import { useProsEntitySyncStateQuery } from "@libs/api/gateways/tg/tg-ops-gateway.hooks";

interface EntitySnapshotsProps {
  tenant: Tenant;
}
const NOT_AVAILABLE = "NA";
const expectedSnapshots = [
  "dbo.AppointmentType",
  "dbo.Availability",
  "dbo.OrgUnit",
  "dbo.Provider",
  "dbo.ProviderOrgUnit",
  "dbo.User",
  "dbo.UserPreference",
  "dbo.UserRole",
  "Replica.AppointmentTypes",
  "Replica.Availability",
  "Replica.Locations",
  "Replica.Practice",
  "Replica.Provider_Location",
  "Replica.UserPermissions",
  "Replica.UserPreferences",
  "Replica.Users"
];
export const EntitySnapshots = ({ tenant }: EntitySnapshotsProps) => {
  const [filterCurrentValue, setFilterCurrentValue] = useState<string>("");
  const [filter, setFilter] = useState<string>("");
  const [filterError, setFilterError] = useState<string>();
  const theme = useTheme();
  const {
    data: entitySyncState,
    isLoading,
    error
  } = useProsEntitySyncStateQuery(tenant.id);

  const { entitySnapshots = [] } = entitySyncState || {};
  const filteredSnapshots = useMemo(() => {
    try {
      const regExp = new RegExp(filter, "i");

      const missingSnapshots: EntitySnapshot[] = expectedSnapshots
        .filter(
          expected =>
            !entitySnapshots.find(
              e => e.entityType.toLowerCase() === expected.toLowerCase()
            )
        )
        .map(e => {
          return {
            tenantId: tenant.id,
            entityType: e,
            snapshotId: NOT_AVAILABLE,
            processingSnapshotId: NOT_AVAILABLE
          };
        });

      const mergedResult = entitySnapshots
        .concat(missingSnapshots)
        .sort((a, b) => a.entityType.localeCompare(b.entityType));

      const filteredSnapshots = mergedResult?.filter(snapshot =>
        regExp.test(snapshot.entityType)
      );
      return filteredSnapshots;
    } catch (e) {
      setFilterError(String(e));
      return [];
    }
  }, [entitySnapshots, filter, tenant.id]);

  return (
    <Stack>
      <Stack verticalAlign="center" tokens={{ childrenGap: 8 }}>
        <StackItem>
          <Heading variant="heading">Snapshots</Heading>
          <Stack horizontal tokens={{ childrenGap: theme.spacing.m }}>
            <SearchBox
              id="snapshot-search"
              value={filterCurrentValue}
              onChange={(_e, value) => setFilterCurrentValue(value)}
              onSearch={setFilter}
              placeholder="Filter snapshots"
              removeSpecialCharacters={false}
            />

            {filterError && (
              <Stack>
                <MessageBar messageBarType={MessageBarType.error}>
                  {filterError}
                </MessageBar>
              </Stack>
            )}
          </Stack>
        </StackItem>
        <StackItem>
          <QueryStateIndicator
            isLoading={isLoading}
            data={filteredSnapshots}
            error={error}
            loadingTitle="Loading snapshots"
          >
            {data => {
              const snapShotDisplayItems: DisplayEntitySnapshot[] = data.map(
                es => {
                  let snapshotStatus: SnapshotStatus;
                  if (es.snapshotId === NOT_AVAILABLE) {
                    snapshotStatus = SnapshotStatus.NotStarted;
                  } else if (es.snapshotId !== es.processingSnapshotId) {
                    snapshotStatus = SnapshotStatus.Processing;
                  } else snapshotStatus = SnapshotStatus.Processed;

                  return {
                    entityType: es.entityType,
                    snapshotActive: es.snapshotActive,
                    currentSnapshotActivatedMilliseconds: es.snapshotActive
                      ? DateTime.now()
                          .diff(es.snapshotActive)
                          .as("milliseconds")
                      : undefined,
                    isProcessing:
                      es.snapshotId === NOT_AVAILABLE
                        ? true
                        : es.snapshotId !== es.processingSnapshotId,

                    status: snapshotStatus,
                    processingDurationToNowMilliseconds: es.processingStarted
                      ? DateTime.now()
                          .diff(es.processingStarted)
                          .as("milliseconds")
                      : undefined,
                    processingDurationFromActiveMilliseconds:
                      es.processingStarted && es.snapshotActive
                        ? es.snapshotActive
                            .diff(es.processingStarted)
                            .as("milliseconds")
                        : undefined,
                    snapshotId: es.snapshotId,
                    processingSnapshotId: es.processingSnapshotId
                  };
                }
              );
              return (
                <EntitySnapshotTable snapshotItems={snapShotDisplayItems} />
              );
            }}
          </QueryStateIndicator>
        </StackItem>
      </Stack>
    </Stack>
  );
};
